import type { Catalog } from '@supernova/shared';

/** Países de América para “decorar” el picker del demo (solo lectura visual). */
const AMERICA_DEMO_LABELS: Array<{ country: string; name: string; uuid: string; currency: string }> = [
  { country: 'AR', name: 'Catálogo Argentina', uuid: 'a0000000-0000-4000-8000-000000000001', currency: 'ARS' },
  { country: 'CL', name: 'Catálogo Chile', uuid: 'a0000000-0000-4000-8000-000000000002', currency: 'CLP' },
  { country: 'US', name: 'Catálogo USA', uuid: 'a0000000-0000-4000-8000-000000000003', currency: 'USD' },
  { country: 'CO', name: 'Catálogo Colombia', uuid: 'a0000000-0000-4000-8000-000000000004', currency: 'COP' },
  { country: 'PE', name: 'Catálogo Perú', uuid: 'a0000000-0000-4000-8000-000000000005', currency: 'PEN' },
  { country: 'BR', name: 'Catálogo Brasil', uuid: 'a0000000-0000-4000-8000-000000000006', currency: 'BRL' },
  { country: 'UY', name: 'Catálogo Uruguay', uuid: 'a0000000-0000-4000-8000-000000000007', currency: 'UYU' },
  { country: 'PY', name: 'Catálogo Paraguay', uuid: 'a0000000-0000-4000-8000-000000000008', currency: 'PYG' },
  { country: 'BO', name: 'Catálogo Bolivia', uuid: 'a0000000-0000-4000-8000-000000000009', currency: 'BOB' },
  { country: 'EC', name: 'Catálogo Ecuador', uuid: 'a0000000-0000-4000-8000-00000000000a', currency: 'USD' },
  { country: 'CR', name: 'Catálogo Costa Rica', uuid: 'a0000000-0000-4000-8000-00000000000b', currency: 'CRC' },
  { country: 'PA', name: 'Catálogo Panamá', uuid: 'a0000000-0000-4000-8000-00000000000c', currency: 'USD' },
  { country: 'GT', name: 'Catálogo Guatemala', uuid: 'a0000000-0000-4000-8000-00000000000d', currency: 'GTQ' },
  { country: 'DO', name: 'Catálogo República Dominicana', uuid: 'a0000000-0000-4000-8000-00000000000e', currency: 'DOP' },
];

const COUNTRY_CURRENCY: Record<string, string> = Object.fromEntries(
  AMERICA_DEMO_LABELS.map((r) => [r.country, r.currency]),
);
COUNTRY_CURRENCY.MX = 'MXN';

/**
 * Catálogos privados de muestra (página /catalogs en demo).
 * Nombres inventados por tipo de obra — solo visual, sin fila en BD.
 */
const DEMO_PRIVATE_CATALOGS: Catalog[] = [
  {
    id: 'd0000000-0000-4000-8000-000000000001',
    tenantId: '00000000-0000-0000-0000-0000000ec050',
    kind: 'tenant_upload',
    name: 'Obra civil — Residencial Los Álamos',
    displayName: null,
    description: 'Tabulador interno de vivienda unifamiliar (demostración)',
    country: null,
    currency: 'MXN',
    status: 'ready',
    resourcesCount: 12_450,
    compuestosCount: 1_820,
    resourcesSinUnidad: 0, resourcesSinPrecio: 0, lastSyncError: null, embeddingProgress: null,
    lastSyncedAt: null,
    createdAt: '2026-03-01T12:00:00.000Z',
    updatedAt: '2026-03-01T12:00:00.000Z',
  },
  {
    id: 'd0000000-0000-4000-8000-000000000002',
    tenantId: '00000000-0000-0000-0000-0000000ec050',
    kind: 'tenant_upload',
    name: 'Instalaciones MEP — Torre Corporativa',
    displayName: null,
    description: 'Hidráulica, eléctrica y aire acondicionado (demostración)',
    country: null,
    currency: 'MXN',
    status: 'ready',
    resourcesCount: 8_760,
    compuestosCount: 940,
    resourcesSinUnidad: 0, resourcesSinPrecio: 0, lastSyncError: null, embeddingProgress: null,
    lastSyncedAt: null,
    createdAt: '2026-03-01T12:00:00.000Z',
    updatedAt: '2026-03-01T12:00:00.000Z',
  },
  {
    id: 'd0000000-0000-4000-8000-000000000003',
    tenantId: '00000000-0000-0000-0000-0000000ec050',
    kind: 'tenant_upload',
    name: 'Infraestructura vial — Conservación 2026',
    displayName: null,
    description: 'Carreteras, señales y obra de mantenimiento (demostración)',
    country: null,
    currency: 'MXN',
    status: 'ready',
    resourcesCount: 15_230,
    compuestosCount: 2_110,
    resourcesSinUnidad: 0, resourcesSinPrecio: 0, lastSyncError: null, embeddingProgress: null,
    lastSyncedAt: null,
    createdAt: '2026-03-01T12:00:00.000Z',
    updatedAt: '2026-03-01T12:00:00.000Z',
  },
];

const SYNTHETIC_IDS = new Set(AMERICA_DEMO_LABELS.map((x) => x.uuid));
const DEMO_PRIVATE_IDS = new Set(DEMO_PRIVATE_CATALOGS.map((c) => c.id));

/**
 * Nombre visible en el demo. Oculta basura de tenant y renombra referencia.
 */
export function demoCatalogDisplayName(c: Catalog): string {
  if (c.kind === 'ecostos' || /^ecostos$/i.test(c.name.trim())) return 'ECOSTOS';
  const n = c.name.trim();
  if (/bdfacsa|dbfacsa/i.test(n)) return 'DBFACSA';
  const country = c.country;
  if (country) {
    const hit = AMERICA_DEMO_LABELS.find((x) => x.country === country.toUpperCase());
    if (hit) return hit.name;
  }
  return n;
}

/**
 * Catálogos que se muestran en el presupuesto del demo:
 * - ECOSTOS + referencias de sistema (renombradas)
 * - sin uploads de tenant (TEST CAT, CONAGUA, etc.)
 * - relleno visual con países de América (ids sintéticos; el picker está disabled en demo)
 */
export function buildDemoCatalogList(real: Catalog[]): Catalog[] {
  const system = real.filter(
    (c) => c.kind === 'ecostos' || (c.kind === 'referencia' && c.tenantId == null),
  );

  const renamed = system.map((c) => ({
    ...c,
    displayName: demoCatalogDisplayName(c),
  }));

  const usedCountries = new Set(
    renamed.map((c) => (c.country ?? '').toUpperCase()).filter(Boolean),
  );
  usedCountries.add('MX');

  const synthetics: Catalog[] = [];
  for (const row of AMERICA_DEMO_LABELS) {
    if (usedCountries.has(row.country)) continue;
    if (renamed.some((c) => c.name === row.name)) continue;
    synthetics.push({
      id: row.uuid,
      tenantId: null,
      kind: 'referencia',
      name: row.name,
      displayName: row.name,
      description: 'Catálogo de muestra (demostración)',
      country: row.country,
      currency: row.currency,
      status: 'ready',
      resourcesCount: 28_000 + synthetics.length * 1_700,
      compuestosCount: 900 + synthetics.length * 80,
      resourcesSinUnidad: 0, resourcesSinPrecio: 0, lastSyncError: null, embeddingProgress: null,
      lastSyncedAt: null,
      createdAt: new Date(0).toISOString(),
      updatedAt: new Date(0).toISOString(),
    });
    if (synthetics.length >= 8) break;
  }

  const withCurrency = renamed.map((c) => {
    if (c.currency) return c;
    const cc = (c.country ?? '').toUpperCase();
    const cur = COUNTRY_CURRENCY[cc];
    return cur ? { ...c, currency: cur } : c;
  });

  withCurrency.sort((a, b) => {
    const ae = a.kind === 'ecostos' ? 0 : 1;
    const be = b.kind === 'ecostos' ? 0 : 1;
    if (ae !== be) return ae - be;
    return a.name.localeCompare(b.name, 'es');
  });

  return [...withCurrency, ...synthetics];
}

/** Lista para la página /catalogs en demo guest: banderas + privados inventados. */
export function catalogsForDemoPage(real: Catalog[]): { referencia: Catalog[]; propios: Catalog[] } {
  return {
    referencia: buildDemoCatalogList(real),
    // No mezclar uploads reales del tenant Ecosoft (MAYA, etc.): el demo muestra solo muestra.
    propios: DEMO_PRIVATE_CATALOGS,
  };
}

export function isDemoSyntheticCatalogId(id: string): boolean {
  return SYNTHETIC_IDS.has(id);
}

/** Ids solo visuales (sin detalle en API): relleno por país + privados inventados. */
export function isDemoDisplayOnlyCatalogId(id: string): boolean {
  return SYNTHETIC_IDS.has(id) || DEMO_PRIVATE_IDS.has(id);
}

export function findEcostosCatalogId(catalogs: Catalog[]): string | null {
  const hit = catalogs.find(
    (c) => c.kind === 'ecostos' || /^ecostos$/i.test(c.name.trim()),
  );
  return hit?.id ?? null;
}
